import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../../store/hooks';
import { selectCurrentApplication } from '../../../store/slices/application.slice';
import MainLayout from '../../../components/layout/MainLayout';
import TimeRangeSelector, { TimeRange } from '../../../components/common/TimeRangeSelector';
import {
  DevicePhoneMobileIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  FunnelIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlayIcon,
  HandRaisedIcon,
  ArrowsPointingOutIcon,
  ArrowsUpDownIcon,
  FireIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import rumService, { RUMSession } from '../../../services/api/rum.service';
import { journeysService, PopularPage, HeatmapData, ScrollDepthData } from '../../../services/api/journeys.service';

interface MobileSession extends RUMSession {
  device_model?: string;
  app_version?: string;
  os_version?: string;
  crash_count?: number;
}

interface TouchHeatmapPoint {
  x: number;
  y: number;
  type: 'tap' | 'longPress' | 'swipe' | 'scroll';
  intensity: number;
  count: number;
}

export default function MobileJourneysPage() {
  const { t } = useTranslation('mobile');
  const navigate = useNavigate();
  const selectedAppId = useAppSelector(selectCurrentApplication);

  // State
  const [sessions, setSessions] = useState<MobileSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
  const [isProcessingBatch, setIsProcessingBatch] = useState(false);
  const [totalSessions, setTotalSessions] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);

  // Tabs
  const [activeTab, setActiveTab] = useState<'sessions' | 'heatmap'>('sessions');

  // Time range for heatmap
  const [timeRange, setTimeRange] = useState<TimeRange>({
    startTime: Date.now() - 7 * 24 * 60 * 60 * 1000,
    endTime: Date.now(),
    rangeType: 'preset',
    presetValue: '7d',
  });

  // Heatmap State
  const [screens, setScreens] = useState<PopularPage[]>([]);
  const [selectedScreen, setSelectedScreen] = useState<string>('');
  const [heatmapData, setHeatmapData] = useState<HeatmapData | null>(null);
  const [scrollDepthData, setScrollDepthData] = useState<ScrollDepthData | null>(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapFilter, setHeatmapFilter] = useState<'all' | 'tap' | 'swipe' | 'scroll'>('all');

  // Phone mockup canvas
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Filters
  const [filters, setFilters] = useState({
    platform: 'mobile' as 'mobile' | 'ios' | 'android',
    hasErrors: undefined as boolean | undefined,
    dateRange: '7d',
  });

  // Stats
  const [stats, setStats] = useState({
    totalSessions: 0,
    activeSessions: 0,
    crashes: 0,
    avgSessionDuration: 0,
  });

  // Load sessions
  const loadSessions = useCallback(async () => {
    if (!selectedAppId || selectedAppId === 'all') {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const endTime = new Date().toISOString();
      let startTime: string;
      switch (filters.dateRange) {
        case '24h':
          startTime = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
          break;
        case '7d':
          startTime = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
          break;
        case '30d':
          startTime = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
          break;
        default:
          startTime = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      }

      const response = await rumService.getSessions({
        applicationId: selectedAppId,
        startTime,
        endTime,
        platform: filters.platform === 'mobile' ? undefined : filters.platform,
        hasErrors: filters.hasErrors,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      });

      const mobileSessions = response.sessions.filter((s) => {
        const platform = (s.platform || '').toLowerCase();
        return (
          platform.includes('ios') ||
          platform.includes('android') ||
          platform.includes('react-native') ||
          platform.includes('react native') ||
          platform.includes('reactnative') ||
          platform.includes('flutter') ||
          platform.includes('mobile')
        );
      });

      setSessions(mobileSessions as MobileSession[]);
      setTotalSessions(response.total);

      const crashCount = mobileSessions.filter((s) => s.error_count > 0).length;
      const totalDuration = mobileSessions.reduce((acc, s) => acc + (s.duration_ms || 0), 0);

      setStats({
        totalSessions: response.total,
        activeSessions: mobileSessions.filter((s) => !s.ended_at).length,
        crashes: crashCount,
        avgSessionDuration: mobileSessions.length > 0 ? totalDuration / mobileSessions.length : 0,
      });
    } catch (error) {
      console.error('Failed to load mobile sessions:', error);
      toast.error(t('errors.loadFailed') || 'Failed to load mobile sessions');
    } finally {
      setLoading(false);
    }
  }, [selectedAppId, filters, currentPage, pageSize, t]);

  // Load mobile screens for heatmap (using platform=mobile filter)
  const loadScreens = useCallback(async () => {
    if (!selectedAppId || selectedAppId === 'all') return;

    try {
      setHeatmapLoading(true);
      const startTime = new Date(timeRange.startTime);
      const endTime = new Date(timeRange.endTime);

      // Use platform='mobile' to get only mobile app screens, not web pages
      const pages = await journeysService.getPopularPages(
        selectedAppId, startTime, endTime, 20, undefined, 'mobile'
      );
      setScreens(pages);

      if (pages.length > 0 && !selectedScreen) {
        setSelectedScreen(pages[0].url);
      }
    } catch (error) {
      console.error('Failed to load mobile screens:', error);
    } finally {
      setHeatmapLoading(false);
    }
  }, [selectedAppId, timeRange]);

  // Load heatmap detail for selected screen
  const loadHeatmapDetail = useCallback(async () => {
    if (!selectedAppId || selectedAppId === 'all' || !selectedScreen) return;

    try {
      const startTime = new Date(timeRange.startTime);
      const endTime = new Date(timeRange.endTime);

      const [heatmap, scrollData] = await Promise.all([
        journeysService.getHeatmapData(selectedAppId, selectedScreen, startTime, endTime, undefined, 'mobile'),
        journeysService.getScrollDepth(selectedAppId, selectedScreen, startTime, endTime, undefined, 'mobile'),
      ]);

      setHeatmapData(heatmap);
      setScrollDepthData(scrollData);
    } catch (error) {
      console.error('Failed to load heatmap detail:', error);
    }
  }, [selectedAppId, selectedScreen, timeRange]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (activeTab === 'heatmap') {
      loadScreens();
    }
  }, [loadScreens, activeTab]);

  useEffect(() => {
    if (activeTab === 'heatmap') {
      loadHeatmapDetail();
    }
  }, [loadHeatmapDetail, activeTab]);

  // Auto-select first screen
  useEffect(() => {
    if (screens.length > 0 && !selectedScreen) {
      setSelectedScreen(screens[0].url);
    }
  }, [screens]);

  // Convert heatmap API data to touch points for the phone mockup
  const heatmapPoints: TouchHeatmapPoint[] = (() => {
    if (!heatmapData) return [];
    const points: TouchHeatmapPoint[] = [];

    // Map clicks to tap points (normalize x/y to percentage 0-100)
    if (heatmapData.clicks) {
      const clickCounts = new Map<string, number>();
      heatmapData.clicks.forEach((c) => {
        const key = `${Math.round(c.x / 10) * 10}-${Math.round(c.y / 10) * 10}`;
        clickCounts.set(key, (clickCounts.get(key) || 0) + 1);
      });
      const maxCount = Math.max(1, ...clickCounts.values());

      heatmapData.clicks.forEach((click) => {
        // Use pageX/pageY percentages (relative to viewport)
        const xPct = Math.min(100, Math.max(0, (click.x / 400) * 100));
        const yPct = Math.min(100, Math.max(0, (click.y / 800) * 100));
        const key = `${Math.round(click.x / 10) * 10}-${Math.round(click.y / 10) * 10}`;
        const count = clickCounts.get(key) || 1;
        points.push({
          x: xPct,
          y: yPct,
          type: 'tap',
          intensity: Math.max(0.3, count / maxCount),
          count,
        });
      });
    }

    // Map scroll events
    if (heatmapData.scroll) {
      heatmapData.scroll.forEach((s) => {
        points.push({
          x: 90,
          y: Math.min(100, s.depth || 0),
          type: 'scroll',
          intensity: Math.max(0.3, (s.depth || 0) / 100),
          count: 1,
        });
      });
    }

    // Map mouse moves (touch moves on mobile)
    if (heatmapData.mouseMoves) {
      heatmapData.mouseMoves.forEach((m) => {
        const xPct = Math.min(100, Math.max(0, (m.x / 400) * 100));
        const yPct = Math.min(100, Math.max(0, (m.y / 800) * 100));
        points.push({
          x: xPct,
          y: yPct,
          type: 'swipe',
          intensity: 0.4,
          count: 1,
        });
      });
    }

    return points;
  })();

  // Filter heatmap points
  const filteredHeatmapPoints = heatmapPoints.filter(point => {
    if (heatmapFilter === 'all') return true;
    if (heatmapFilter === 'tap') return point.type === 'tap' || point.type === 'longPress';
    if (heatmapFilter === 'swipe') return point.type === 'swipe';
    if (heatmapFilter === 'scroll') return point.type === 'scroll';
    return true;
  });

  // Get selected screen data
  const selectedScreenData = screens.find(s => s.url === selectedScreen);

  // Selection handlers
  const handleSelectSession = (sessionId: string) => {
    setSelectedSessions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedSessions.size === sessions.length) {
      setSelectedSessions(new Set());
    } else {
      setSelectedSessions(new Set(sessions.map((s) => s.session_id)));
    }
  };

  // Batch operations
  const handleBatchDelete = async () => {
    if (selectedSessions.size === 0) return;

    const confirmMessage = t('journeys.sessions.confirmBatchDelete', { count: selectedSessions.size }) ||
      `Are you sure you want to delete ${selectedSessions.size} session(s)?`;
    if (!window.confirm(confirmMessage)) return;

    setIsProcessingBatch(true);
    try {
      await rumService.batchDeleteRecordings(Array.from(selectedSessions));
      setSessions((prev) => prev.filter((session) => !selectedSessions.has(session.session_id)));
      setSelectedSessions(new Set());
      toast.success(t('journeys.sessions.batchDeleteSuccess') || 'Sessions deleted successfully');
    } catch (error) {
      console.error('Failed to delete sessions:', error);
      toast.error(t('journeys.sessions.batchDeleteError') || 'Failed to delete sessions');
    } finally {
      setIsProcessingBatch(false);
    }
  };

  const handleBatchMarkViewed = async (viewed: boolean) => {
    if (selectedSessions.size === 0) return;

    setIsProcessingBatch(true);
    try {
      await rumService.batchToggleViewed(Array.from(selectedSessions), viewed);
      setSessions((prev) =>
        prev.map((session) =>
          selectedSessions.has(session.session_id) ? { ...session, viewed } : session
        )
      );
      setSelectedSessions(new Set());
      toast.success(
        viewed
          ? t('journeys.sessions.markedAsViewed') || 'Marked as viewed'
          : t('journeys.sessions.markedAsUnviewed') || 'Marked as unviewed'
      );
    } catch (error) {
      console.error('Failed to update sessions:', error);
      toast.error(t('journeys.sessions.updateError') || 'Failed to update sessions');
    } finally {
      setIsProcessingBatch(false);
    }
  };

  const handleViewSession = (session: MobileSession) => {
    navigate(`/analytics/mobile/session/${session.session_id}`);
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const getPlatformIcon = (platform: string) => {
    const p = (platform || '').toLowerCase();
    if (p.includes('react')) return '⚛️';
    if (p.includes('flutter')) return '🦋';
    if (p.includes('ios')) return '🍎';
    if (p.includes('android')) return '🤖';
    return '📱';
  };

  const getPlatformName = (platform: string) => {
    const p = (platform || '').toLowerCase();
    if (p.includes('react')) return 'React Native';
    if (p.includes('flutter')) return 'Flutter';
    if (p.includes('ios')) return 'iOS';
    if (p.includes('android')) return 'Android';
    return platform || 'Mobile';
  };

  const totalPages = Math.ceil(totalSessions / pageSize);

  const getTouchColor = (type: string, intensity: number): string => {
    const alpha = intensity * 0.8;
    switch (type) {
      case 'tap':
        return `rgba(168, 85, 247, ${alpha})`;
      case 'longPress':
        return `rgba(239, 68, 68, ${alpha})`;
      case 'swipe':
        return `rgba(34, 197, 94, ${alpha})`;
      case 'scroll':
        return `rgba(59, 130, 246, ${alpha})`;
      default:
        return `rgba(168, 85, 247, ${alpha})`;
    }
  };

  // Extract short screen name from URL/path
  const getScreenName = (url: string) => {
    if (!url) return 'Unknown';
    // If it's a mobile screen name (not a URL), return as-is
    if (!url.startsWith('http')) return url;
    try {
      const u = new URL(url);
      return u.pathname || url;
    } catch {
      return url;
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="relative overflow-hidden bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-rose-500/10 dark:from-purple-500/5 dark:via-pink-500/5 dark:to-rose-500/5 rounded-2xl p-8 border border-purple-200/50 dark:border-purple-800/50">
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl shadow-lg">
                <DevicePhoneMobileIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {t('journeys.title') || 'Mobile Journeys'}
              </h1>
            </div>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-2xl">
              {t('journeys.subtitle') || 'Track and analyze mobile user sessions, crashes, and app performance'}
            </p>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-400/20 to-pink-600/20 rounded-full blur-3xl -z-0" />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('journeys.stats.totalSessions') || 'Total Sessions'}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalSessions.toLocaleString()}</p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                <DevicePhoneMobileIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('journeys.stats.activeSessions') || 'Active Sessions'}</p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.activeSessions}</p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                <PlayIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('journeys.stats.crashes') || 'Crashes'}</p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.crashes}</p>
              </div>
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-xl">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('journeys.stats.avgDuration') || 'Avg. Duration'}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatDuration(stats.avgSessionDuration)}</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                <ClockIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Tab Selector */}
        <div className="flex items-center gap-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2">
          <button
            onClick={() => setActiveTab('sessions')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'sessions'
                ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/30'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <TableCellsIcon className="w-5 h-5" />
            {t('journeys.tabs.sessions') || 'Sessions'}
          </button>
          <button
            onClick={() => setActiveTab('heatmap')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === 'heatmap'
                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/30'
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <FireIcon className="w-5 h-5" />
            {t('journeys.tabs.heatmap') || 'Touch Heatmap'}
          </button>
        </div>

        {/* Heatmap View */}
        {activeTab === 'heatmap' && (
          <div className="space-y-6">
            {/* Time Range */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
              <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Screen List */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {t('heatmap.screens') || 'Screens'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('heatmap.selectScreen') || 'Select a screen to view touch data'}
                  </p>
                </div>
                <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
                  {heatmapLoading ? (
                    <div className="p-8 flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                    </div>
                  ) : screens.length === 0 ? (
                    <div className="p-8 text-center">
                      <FireIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        {t('heatmap.noData') || 'No mobile screens with touch data found. Make sure your mobile SDK has heatmap tracking enabled.'}
                      </p>
                    </div>
                  ) : (
                    screens.map((screen) => (
                      <button
                        key={screen.url}
                        onClick={() => setSelectedScreen(screen.url)}
                        className={`w-full p-4 text-left transition-colors ${
                          selectedScreen === screen.url
                            ? 'bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-600'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                        }`}
                      >
                        <div className="font-medium text-gray-900 dark:text-white text-sm truncate">
                          {getScreenName(screen.url)}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <HandRaisedIcon className="w-3 h-3 text-purple-500" />
                            {screen.views} views
                          </span>
                          <span className="flex items-center gap-1">
                            <EyeIcon className="w-3 h-3 text-blue-500" />
                            {screen.uniqueVisitors} users
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Heatmap Preview */}
              <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {selectedScreen ? getScreenName(selectedScreen) : t('heatmap.selectScreenTitle') || 'Select a screen'}
                    </h3>
                    {selectedScreenData && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {t('heatmap.avgTime') || 'Avg. time'}: {formatDuration(selectedScreenData.avgTime)}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    <button
                      onClick={() => setHeatmapFilter('all')}
                      className={`px-3 py-1.5 rounded text-sm transition-colors ${
                        heatmapFilter === 'all' ? 'bg-purple-600 text-white' : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setHeatmapFilter('tap')}
                      className={`px-3 py-1.5 rounded text-sm transition-colors ${
                        heatmapFilter === 'tap' ? 'bg-purple-600 text-white' : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Taps
                    </button>
                    <button
                      onClick={() => setHeatmapFilter('swipe')}
                      className={`px-3 py-1.5 rounded text-sm transition-colors ${
                        heatmapFilter === 'swipe' ? 'bg-green-600 text-white' : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Swipes
                    </button>
                    <button
                      onClick={() => setHeatmapFilter('scroll')}
                      className={`px-3 py-1.5 rounded text-sm transition-colors ${
                        heatmapFilter === 'scroll' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      Scroll
                    </button>
                  </div>
                </div>

                {/* Phone Mockup with Heatmap */}
                <div className="p-8 flex justify-center bg-gray-50 dark:bg-gray-900/50">
                  {screens.length === 0 && !heatmapLoading ? (
                    <div className="text-center py-12">
                      <DevicePhoneMobileIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        {t('heatmap.noMobileData') || 'No mobile touch data yet'}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
                        {t('heatmap.noMobileDataDesc') || 'Touch interactions will appear here once users start using your mobile app with the SDK installed and heatmap tracking enabled.'}
                      </p>
                    </div>
                  ) : (
                    <div className="relative w-64">
                      {/* Phone Frame */}
                      <div className="bg-gray-900 rounded-[2rem] p-2 shadow-2xl">
                        <div className="bg-black rounded-[1.75rem] overflow-hidden">
                          {/* Status Bar */}
                          <div className="bg-black h-6 flex items-center justify-between px-5 text-white text-[10px]">
                            <span>9:41</span>
                            <div className="w-16 h-4 bg-black rounded-b-lg absolute left-1/2 -translate-x-1/2 top-2"></div>
                            <span>100%</span>
                          </div>

                          {/* Screen Content */}
                          <div className="relative bg-white aspect-[9/19] overflow-hidden">
                            {/* Simulated UI */}
                            <div className="absolute inset-0 p-3">
                              <div className="space-y-3">
                                <div className="h-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg"></div>
                                <div className="space-y-2">
                                  <div className="h-16 bg-gray-100 rounded-lg"></div>
                                  <div className="h-16 bg-gray-100 rounded-lg"></div>
                                  <div className="h-16 bg-gray-100 rounded-lg"></div>
                                </div>
                                <div className="h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full"></div>
                              </div>
                            </div>

                            {/* Heatmap Overlay */}
                            <div className="absolute inset-0 pointer-events-none">
                              {filteredHeatmapPoints.map((point, index) => (
                                <div
                                  key={index}
                                  className="absolute"
                                  style={{
                                    left: `${point.x}%`,
                                    top: `${point.y}%`,
                                    width: point.type === 'scroll' ? '10px' : `${30 + point.count}px`,
                                    height: point.type === 'scroll' ? `${20 + point.intensity * 30}px` : `${30 + point.count}px`,
                                    transform: 'translate(-50%, -50%)',
                                    background: point.type === 'scroll'
                                      ? `linear-gradient(to bottom, ${getTouchColor(point.type, point.intensity)}, transparent)`
                                      : `radial-gradient(circle, ${getTouchColor(point.type, point.intensity)} 0%, transparent 70%)`,
                                    filter: point.type !== 'scroll' ? 'blur(8px)' : 'none',
                                    opacity: point.intensity,
                                  }}
                                ></div>
                              ))}
                            </div>

                            {/* Scroll Depth Indicator */}
                            {heatmapFilter === 'scroll' && scrollDepthData && scrollDepthData.total > 0 && (
                              <div className="absolute right-1 top-10 bottom-10 w-1.5 bg-gradient-to-b from-green-500 via-yellow-500 to-red-500 rounded-full opacity-60">
                                <div
                                  className="absolute left-1/2 -translate-x-1/2 w-3 h-0.5 bg-white shadow"
                                  style={{ top: `${100 - (scrollDepthData.avgDepth || 0)}%` }}
                                ></div>
                              </div>
                            )}
                          </div>

                          {/* Home Indicator */}
                          <div className="bg-black h-6 flex items-center justify-center">
                            <div className="w-24 h-1 bg-gray-700 rounded-full"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Stats + Legend */}
                {selectedScreen && heatmapData && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                    {/* Touch Stats */}
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-purple-600">{heatmapData.clicks?.length || 0}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Taps</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600">{heatmapData.mouseMoves?.length || 0}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Swipes</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{scrollDepthData ? `${scrollDepthData.avgDepth.toFixed(0)}%` : '-'}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Avg Scroll</p>
                      </div>
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap items-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-purple-500/60"></div>
                        <span className="text-gray-600 dark:text-gray-400">Taps</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-green-500/60"></div>
                        <span className="text-gray-600 dark:text-gray-400">Swipes</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-blue-500/60"></div>
                        <span className="text-gray-600 dark:text-gray-400">Scroll Depth</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Scroll Depth Distribution */}
                {scrollDepthData && scrollDepthData.total > 0 && (
                  <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Scroll Depth Distribution</h4>
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { depth: '0-25%', users: scrollDepthData.distribution['0-25'], color: 'bg-red-500' },
                        { depth: '25-50%', users: scrollDepthData.distribution['25-50'], color: 'bg-orange-500' },
                        { depth: '50-75%', users: scrollDepthData.distribution['50-75'], color: 'bg-yellow-500' },
                        { depth: '75-100%', users: scrollDepthData.distribution['75-100'], color: 'bg-green-500' },
                      ].map((bucket) => {
                        const pct = scrollDepthData.total > 0 ? (bucket.users / scrollDepthData.total) * 100 : 0;
                        return (
                          <div key={bucket.depth} className="text-center">
                            <div className="text-sm font-bold text-gray-900 dark:text-white">{pct.toFixed(0)}%</div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
                              <div className={`h-1.5 rounded-full ${bucket.color}`} style={{ width: `${pct}%` }} />
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{bucket.depth}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sessions View - Filters and Actions */}
        {activeTab === 'sessions' && (
          <>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <FunnelIcon className="w-5 h-5 text-gray-400" />
                <select
                  value={filters.platform}
                  onChange={(e) => setFilters({ ...filters, platform: e.target.value as any })}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="mobile">{t('journeys.filters.allPlatforms') || 'All Platforms'}</option>
                  <option value="ios">iOS</option>
                  <option value="android">Android</option>
                </select>
              </div>

              <select
                value={filters.dateRange}
                onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
                className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="24h">{t('journeys.filters.last24h') || 'Last 24 hours'}</option>
                <option value="7d">{t('journeys.filters.last7d') || 'Last 7 days'}</option>
                <option value="30d">{t('journeys.filters.last30d') || 'Last 30 days'}</option>
              </select>

              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <input
                  type="checkbox"
                  checked={filters.hasErrors === true}
                  onChange={(e) => setFilters({ ...filters, hasErrors: e.target.checked ? true : undefined })}
                  className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                />
                {t('journeys.filters.withErrors') || 'With errors only'}
              </label>

              <button
                onClick={loadSessions}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                title={t('journeys.refresh') || 'Refresh'}
              >
                <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Batch Actions */}
            {selectedSessions.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedSessions.size} {t('journeys.selected') || 'selected'}
                </span>
                <button
                  onClick={() => handleBatchMarkViewed(true)}
                  disabled={isProcessingBatch}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 disabled:opacity-50"
                >
                  <EyeIcon className="w-4 h-4" />
                  {t('journeys.actions.markViewed') || 'Mark Viewed'}
                </button>
                <button
                  onClick={() => handleBatchMarkViewed(false)}
                  disabled={isProcessingBatch}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  <EyeSlashIcon className="w-4 h-4" />
                  {t('journeys.actions.markUnviewed') || 'Mark Unviewed'}
                </button>
                <button
                  onClick={handleBatchDelete}
                  disabled={isProcessingBatch}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50"
                >
                  <TrashIcon className="w-4 h-4" />
                  {t('journeys.actions.delete') || 'Delete'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sessions Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-12">
              <DevicePhoneMobileIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {t('journeys.noSessions') || 'No mobile sessions found'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('journeys.noSessionsDescription') || 'Mobile sessions will appear here once users start using your app with the SDK installed.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedSessions.size === sessions.length && sessions.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('journeys.table.platform') || 'Platform'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('journeys.table.device') || 'Device'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('journeys.table.started') || 'Started'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('journeys.table.duration') || 'Duration'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('journeys.table.screens') || 'Screens'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('journeys.table.errors') || 'Errors'}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('journeys.table.status') || 'Status'}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('journeys.table.actions') || 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {sessions.map((session) => (
                    <tr
                      key={session.session_id}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-900/50 ${
                        !session.viewed ? 'bg-purple-50/30 dark:bg-purple-900/10' : ''
                      }`}
                    >
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedSessions.has(session.session_id)}
                          onChange={() => handleSelectSession(session.session_id)}
                          className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{getPlatformIcon(session.platform)}</span>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white text-sm">
                              {getPlatformName(session.platform)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {session.os_version || session.browser_version || '-'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {session.device_type || session.browser_name || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {session.os_name || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {new Date(session.started_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {formatDuration(session.duration_ms)}
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                          {session.page_view_count}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {session.error_count > 0 ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                            {session.error_count}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            0
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {!session.ended_at ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            <span className="w-1.5 h-1.5 mr-1.5 rounded-full bg-green-500 animate-pulse"></span>
                            {t('journeys.status.active') || 'Active'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400">
                            {t('journeys.status.ended') || 'Ended'}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <button
                          onClick={() => handleViewSession(session)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                        >
                          <EyeIcon className="w-4 h-4 mr-1" />
                          {t('journeys.actions.view') || 'View'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {sessions.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {t('journeys.pagination.showing') || 'Showing'} {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalSessions)} {t('journeys.pagination.of') || 'of'} {totalSessions}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}
